import { Box, Typography } from "@mui/material";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { formatCorrectPercentage } from "../../utils/helper";
import useParamValues from "../../utils/useParamValues";
import StudentCohortCard from "../../components/studentCohortCard/StudentCohortCard";
import clockIcon from "../../assets/clock_img.svg";
import percentageIcon from "../../assets/percentage.svg";
import soundWaveIcon from "../../assets/sound_wave.svg";
import useTranslationSetup from "../../utils/useTranslationSetup";
import StudentCohortResultBtn from "./studentCohortResultBtn/StudentCohortResultBtn";
import CohortAudioPlayer from "../../components/CohortAudioPlayer/CohortAudioPlayer";
import AnimatedGifText from "../../components/AnimatedGifText/AnimatedGifText";
import { MetricItem } from "../../components/CohortBottomDrawer/CohortMetricItem";
import { useCohortAudioPlayer } from "../../utils/useCohortAudioPlayer";
import {
  cohortDetailsMap,
  cohortIdMap,
  cohortStaticData,
} from "../../utils/CommonUtils/constants";
import "./styles.css";
import CohortViewHeader from "../../components/CohortViewHeader/CohortViewHeader";
import { produceEventLog } from "../../utils/helper";
import { getUserProperties } from "../../utils/helper";

const StudentCohortResultScreen = ({
  assessmentSetResponse,
  onSelectAssessment,
}) => {
  const { t } = useTranslationSetup();
  const { studentName, classNum, section } = useParamValues();
  const cohortNumber = assessmentSetResponse.cohort;
  const cohortKey = cohortIdMap[cohortNumber];

  const cohortContent = cohortStaticData[cohortNumber];
  const cohortDetails = cohortDetailsMap[cohortKey];

  const [isPlaying, playAudio, stopAudio, audioRef] = useCohortAudioPlayer(
    cohortContent?.audio
  );
  const location = useLocation();
  const navigate = useNavigate();
  const showBack = location.state?.showBack;
  const userProperties = getUserProperties(useParamValues());

  useEffect(() => {
    produceEventLog("results_2a_landing_page_view", {
      ...userProperties,
    });
  }, []);

  const primary_assessment_info =
    assessmentSetResponse?.assessments_result.find(
      (a) => a.assessment_type === "primary"
    );

  const secondary_assessment_info =
    assessmentSetResponse?.assessments_result.find(
      (a) => a.assessment_type === "secondary"
    );

  const grade = parseInt(classNum);
  const result = primary_assessment_info.result;
  let isWow = false;

  if (grade === 3 || grade === 4) {
    isWow = result["cwpm"] >= 50;
  } else if (grade === 6 || grade === 7) {
    isWow = result["cwpm"] >= 60;
  }

  const onClickStudentCohortResultBtn = (assessmentId, eventName) => {
    onSelectAssessment(assessmentId);
    produceEventLog(eventName, {
      ...userProperties,
    });
  };

  return (
    <>
      <Box className="cohort-main-screen-header-wrapper">
        <CohortViewHeader onBack={() => navigate(-1)} showBack={showBack} />
      </Box>
      <Box className="student-cohort-result-heading">
        <Typography className="student-cohort-result-student-name">
          {studentName}&apos;s
        </Typography>
        <Typography className="student-cohort-result-class-section">
          {t("class")} - {classNum}
          {section}
        </Typography>
      </Box>
      <Typography className="student-cohort-result-assessment-header">
        {t("student_cohort_result_assessment_header", {
          assessmentNumber: 2,
        })}
      </Typography>
      <Box className={`${cohortContent.className}-bg-color`}>
        <Box
          className={`cohort-oval-color ${cohortContent?.className}-oval-color`}
        >
          <Typography className="cohort-title">
            {cohortContent.title}
          </Typography>
          <Box
            component="img"
            src={cohortContent.icon}
            alt={cohortContent.title}
            className="cohort-image"
          />
        </Box>
        <Box className="cohort-media">
          <CohortAudioPlayer
            isPlaying={isPlaying}
            playAudio={playAudio}
            stopAudio={stopAudio}
            audioRef={audioRef}
            buttonStyle={{
              backgroundColor: "#A2C357",
              minWidth: 0,
              padding: "4px",
              borderRadius: "32px",
            }}
            iconStyle={{ color: "#FFFFFF", width: "20px", height: "20px" }}
          />
          {cohortContent.gifTextArray && (
            <AnimatedGifText
              textArray={cohortContent.gifTextArray}
              pauseDuration={cohortContent.pauseDuration || 150}
              speed={cohortContent.gifSpeed}
            />
          )}
        </Box>
        <Box>
          {cohortContent.gifText && (
            <Typography className="cohort-gif-text">
              {cohortContent.gifText}
            </Typography>
          )}
        </Box>
        <Box className="cohort-metrics-wrapper">
          <MetricItem
            icon={clockIcon}
            label="समूह की CWPM श्रेणी"
            value={cohortDetails.rangesByGrade[classNum].cwpmRange}
          />
          {/* <MetricItem
            icon={percentageIcon}
            label="समूह की शब्द सटीकता श्रेणी"
            value={cohortDetails.rangesByGrade[classNum].accuracyRange}
          /> */}
          {/* <MetricItem
            icon={soundWaveIcon}
            label="समूह की वर्ण सटीकता श्रेणी"
            value={cohortDetails.extraAccuracyRange}
          /> */}
        </Box>
        {assessmentSetResponse.badge && (
          <Box className="badge-details-box">
            <img
              src={cohortDetails.icon}
              alt="Level"
              className="cohort-note-icon"
            />
            <Typography className="cohort-card-note">
              {cohortDetails.iconNote}
            </Typography>
          </Box>
        )}
        <Box className="student-cohort-result-cohort-card-container">
          <StudentCohortCard
            icon={clockIcon}
            value={secondary_assessment_info.result?.cwpm}
            label={t("correct_words_per_minute_student")}
          />
          <StudentCohortCard
            icon={percentageIcon}
            value={`${formatCorrectPercentage(secondary_assessment_info.result?.correct_percentage)}%`}
            label={t("word_accuracy_percentage_student")}
          />
          <StudentCohortCard
            icon={soundWaveIcon}
            value={`${formatCorrectPercentage(secondary_assessment_info.result?.phoneme_accuracy)}%`}
            label={t("phoneme_accuracy_percentage_student")}
          />
        </Box>
        <Box className="student-cohort-result-assessment-2-btn-container">
          <StudentCohortResultBtn
            assessmentNumber={2}
            onClick={() =>
              onClickStudentCohortResultBtn(
                secondary_assessment_info.id,
                "result_2a_assessment2_view_button"
              )
            }
          />
        </Box>
      </Box>
      <Typography className="student-cohort-result-assessment-header">
        {t("student_cohort_result_assessment_header", {
          assessmentNumber: 1,
        })}
      </Typography>
      <Box className="student-cohort-result-assessment-1-btn-container">
        <Box
          className={`assessment-btn-wrapper ${isWow ? "bg-wow" : "bg-practice"}`}
        >
          <StudentCohortResultBtn
            assessmentNumber={1}
            onClick={() =>
              onClickStudentCohortResultBtn(
                primary_assessment_info.id,
                "result_2a_assessment1_view_button"
              )
            }
          />
        </Box>
      </Box>
    </>
  );
};

export default StudentCohortResultScreen;
